(function (root, factory) {
  var dependency = root.FireCalculatorEngine;
  if (typeof require === 'function') {
    try {
      dependency = require('./fire-calculator-engine');
    } catch (_error) {
      dependency = root.FireCalculatorEngine;
    }
  }
  var engine = factory(dependency);
  if (typeof module === 'object' && module.exports) {
    module.exports = engine;
  }
  root.AdvancedFireEngine = engine;
})(typeof globalThis !== 'undefined' ? globalThis : this, function (fireEngine) {
  function safeRate(rate) {
    var normalized = Number.isFinite(rate) ? rate : 0;
    if (normalized < 0) return 0;
    if (normalized > 99.9) return 99.9;
    return normalized;
  }

  // Withdraw up to grossTarget from a single account (gross basis).
  // Returns the gross taken, net received after tax, and remaining gross demand.
  function withdrawFromAccount(balances, index, grossTarget, taxOrPenalty) {
    if (grossTarget <= 0) {
      return {
        remainingGross: 0,
        netWithdrawn: 0,
        grossWithdrawn: 0
      };
    }

    var rate = safeRate(taxOrPenalty);
    var available = balances[index];
    var grossTaken = Math.min(grossTarget, available);
    balances[index] -= grossTaken;

    return {
      remainingGross: grossTarget - grossTaken,
      netWithdrawn: grossTaken * (1 - rate / 100),
      grossWithdrawn: grossTaken
    };
  }

  // Withdraw up to grossTarget from the 401k account, capped by the bracket gross cap.
  function withdrawFrom401kUpToBracket(balances, grossTarget, bracketGrossCap, taxRate) {
    if (grossTarget <= 0) {
      return {
        remainingGross: 0,
        netWithdrawn: 0,
        grossWithdrawn: 0
      };
    }

    var available = balances[0];
    if (available <= 0) {
      return {
        remainingGross: grossTarget,
        netWithdrawn: 0,
        grossWithdrawn: 0
      };
    }

    var rate = safeRate(taxRate);
    var cap = Math.max(0, Number.isFinite(bracketGrossCap) ? bracketGrossCap : 0);
    var grossLimit = Math.min(available, cap, grossTarget);

    if (grossLimit <= 0) {
      return {
        remainingGross: grossTarget,
        netWithdrawn: 0,
        grossWithdrawn: 0
      };
    }

    balances[0] -= grossLimit;
    return {
      remainingGross: grossTarget - grossLimit,
      netWithdrawn: grossLimit * (1 - rate / 100),
      grossWithdrawn: grossLimit
    };
  }

  // Pre-penalty-free drawdown order: taxable first (capital-gains-friendly),
  // then Roth (tax-free), then 401(k) as a last resort because it incurs both
  // ordinary income tax and a 10% early-withdrawal penalty.
  function runPrePenaltyFreeCascade(balances, grossTarget, taxRate) {
    var sourceTotals = {
      from401k: 0,
      fromRoth: 0,
      fromTaxable: 0
    };

    var remainingGross = grossTarget;

    var take = withdrawFromAccount(balances, 2, remainingGross, taxRate);
    sourceTotals.fromTaxable += take.netWithdrawn;
    remainingGross = take.remainingGross;

    if (remainingGross > 0) {
      take = withdrawFromAccount(balances, 1, remainingGross, 0);
      sourceTotals.fromRoth += take.netWithdrawn;
      remainingGross = take.remainingGross;
    }

    if (remainingGross > 0) {
      take = withdrawFromAccount(balances, 0, remainingGross, taxRate + 10);
      sourceTotals.from401k += take.netWithdrawn;
      remainingGross = take.remainingGross;
    }

    return {
      remainingGross: remainingGross,
      from401k: sourceTotals.from401k,
      fromRoth: sourceTotals.fromRoth,
      fromTaxable: sourceTotals.fromTaxable
    };
  }

  // Post-penalty-free drawdown order: fill the lowest-bracket 401(k) slice first,
  // then taxable (capital-gains-friendly), then 401(k) overflow above the bracket,
  // and finally Roth so tax-free dollars compound as long as possible.
  function runPostPenaltyFreeCascade(balances, grossTarget, taxRate, bracketGrossCap) {
    var sourceTotals = {
      from401k: 0,
      fromRoth: 0,
      fromTaxable: 0
    };

    var remainingGross = grossTarget;

    var take = withdrawFrom401kUpToBracket(balances, remainingGross, bracketGrossCap, taxRate);
    sourceTotals.from401k += take.netWithdrawn;
    remainingGross = take.remainingGross;

    if (remainingGross > 0) {
      take = withdrawFromAccount(balances, 2, remainingGross, taxRate);
      sourceTotals.fromTaxable += take.netWithdrawn;
      remainingGross = take.remainingGross;
    }

    if (remainingGross > 0) {
      take = withdrawFromAccount(balances, 0, remainingGross, taxRate);
      sourceTotals.from401k += take.netWithdrawn;
      remainingGross = take.remainingGross;
    }

    if (remainingGross > 0) {
      take = withdrawFromAccount(balances, 1, remainingGross, 0);
      sourceTotals.fromRoth += take.netWithdrawn;
      remainingGross = take.remainingGross;
    }

    return {
      remainingGross: remainingGross,
      from401k: sourceTotals.from401k,
      fromRoth: sourceTotals.fromRoth,
      fromTaxable: sourceTotals.fromTaxable
    };
  }

  function calculate(input) {
    var balances = [input.initial401k, input.initialRoth, input.initialTaxable];
    var contributions = [input.contribution401k, input.contributionRoth, input.contributionTaxable];
    var contributionMultiplier = input.contributionFrequency === 'monthly' ? 12 : 1;
    var years = [];
    var baseWithdrawal = 0;
    var retirementBalance = 0;

    var penaltyFreeAge = Number.isFinite(input.penaltyFreeAge) ? input.penaltyFreeAge : 59;
    var baseBracketCapGross = Number.isFinite(input.bracketCapGross) ? input.bracketCapGross : 201775;
    var bracketCapGrowth = Number.isFinite(input.bracketCapGrowth) ? input.bracketCapGrowth : input.inflationRate;

    if (fireEngine && fireEngine.normalizeContribution401k) {
      contributions[0] = fireEngine.normalizeContribution401k(contributions[0], contributionMultiplier);
    }

    var totalYears = input.lifeExpectancy - input.currentAge;

    for (var year = 0; year <= totalYears; year++) {
      var age = input.currentAge + year;
      var total = balances.reduce(function (sum, value) { return sum + value; }, 0);

      var record = {
        year: year + 1,
        age: age,
        balances: balances.slice(),
        total: total,
        withdrawn: 0,
        withdrawalTarget: 0,
        withdrawalShortfall: 0,
        withdrawalFrom401k: 0,
        withdrawalFromRoth: 0,
        withdrawalFromTaxable: 0
      };

      if (age < input.retirementAge) {
        for (var j = 0; j < balances.length; j++) {
          var annualContribution = contributions[j] * contributionMultiplier * Math.pow(1 + input.contributionIncrease / 100, year);

          if (input.contributionFrequency === 'monthly') {
            // Monthly mode compounds at r/12 with a deposit at the end of each
            // month, matching fire-calculator-engine.js, so monthly and yearly
            // cadences produce genuinely different balances.
            var monthlyRate = input.preRetirementGrowth / 100 / 12;
            var monthlyContribution = annualContribution / 12;

            for (var month = 0; month < 12; month++) {
              balances[j] = balances[j] * (1 + monthlyRate) + monthlyContribution;
            }
          } else {
            balances[j] = balances[j] * (1 + input.preRetirementGrowth / 100) + annualContribution;
          }
        }
      } else {
        for (var k = 0; k < balances.length; k++) {
          balances[k] *= (1 + input.postRetirementGrowth / 100);
        }

        if (age === input.retirementAge) {
          // retirementBalance is the portfolio the moment work stops: the balance carried
          // into the retirement year, before that year's growth and first withdrawal.
          // baseWithdrawal is the gross amount to withdraw from the portfolio (the "4% rule" amount).
          // Taxes are paid from this gross withdrawal; net spending will be less than this amount.
          retirementBalance = total;
          baseWithdrawal = total * (input.withdrawalRate / 100);
        }

        var yearsInRet = age - input.retirementAge;
        // Inflation-adjust the gross withdrawal target each year.
        var withdrawalTarget = baseWithdrawal * Math.pow(1 + input.inflationRate / 100, yearsInRet);
        var cascadeResult;

        if (age < penaltyFreeAge) {
          cascadeResult = runPrePenaltyFreeCascade(balances, withdrawalTarget, input.taxRate);
        } else {
          // The cap is entered in today's dollars (the current top of the 24% bracket), so it
          // is indexed from today - `year` counts from currentAge, not from the retirement year.
          var bracketGrossCapThisYear = baseBracketCapGross * Math.pow(1 + bracketCapGrowth / 100, year);
          cascadeResult = runPostPenaltyFreeCascade(balances, withdrawalTarget, input.taxRate, bracketGrossCapThisYear);
        }

        // withdrawalTarget: gross amount targeted from the portfolio (e.g. 4% of portfolio)
        // withdrawn: net amount actually received after taxes (spending money)
        // withdrawalShortfall: gross demand that could not be met (portfolio depleted)
        record.withdrawalTarget = withdrawalTarget;
        record.withdrawn = cascadeResult.from401k + cascadeResult.fromRoth + cascadeResult.fromTaxable;
        record.withdrawalShortfall = cascadeResult.remainingGross;
        record.withdrawalFrom401k = cascadeResult.from401k;
        record.withdrawalFromRoth = cascadeResult.fromRoth;
        record.withdrawalFromTaxable = cascadeResult.fromTaxable;
      }

      record.balances = balances.slice();
      record.total = balances.reduce(function (sum, value) { return sum + value; }, 0);
      years.push(record);
    }

    return {
      normalizedContribution401k: contributions[0],
      retirementBalance: retirementBalance,
      baseWithdrawal: baseWithdrawal,
      years: years
    };
  }

  return {
    calculate: calculate,
    withdrawFrom401kUpToBracket: withdrawFrom401kUpToBracket,
    withdrawFromAccount: withdrawFromAccount
  };
});
